/**
 * 65.23 (the owner, decision 149: grenades should "come back on respawn, probably drawing down from some central inventory
 * that could run out"), with GameConfig.grenadeStock: each side's grenades come from a finite stock.
 * - A unit keeps what it was issued (`grenadeIssue`, HE and smoke); its side's stock (`state.grenadeStocks`) starts at
 *   GRENADE_STOCK.refills issues for every unit the side starts with (rounded down to whole grenades).
 * - A respawn tops the unit back up to its issue from the stock — so a drop at a relay or a landing at a beacon does too,
 *   those being respawns — and so does a team crate in reach when the unit asks (the crate is where the side's stock is
 *   handed out, whatever the crate itself holds). Each kind as far as the stock goes; an empty stock gives nothing.
 * - The bots walk to a crate for it (core/supply.ts's errand) when out of both kinds and the side still has some.
 * Pure; with the switch off no unit carries an issue and no side a stock, so nothing here does anything.
 */
import type { GameState, PlayerState, StructureState } from '../types';
import { GRENADE_STOCK } from '../constants/action';
import { structureJob } from './body';
import { nearestCrate, nearestTeamCrate, crateSide, supplyReach } from './supply';
import { servesPool } from './fob';
import { carrierHas, carrierLoad, takeFromCarrier } from './carrierLoad';

export type GrenadeEvent = { kind: 'grenadesRestocked'; unitId: number; he: number; smoke: number; at: 'respawn' | 'crate' };

/** The setup's sizing: each unit carrying grenades keeps them as its issue; each side's stock is `refills` issues a unit. */
export function withGrenadeStocks(state: GameState): GameState {
  const stocks: Record<number, { he: number; smoke: number }> = {};
  const players = state.players.map(p => {
    if (!p.grenades) return p;
    const s = stocks[p.teamIndex] ?? (stocks[p.teamIndex] = { he: 0, smoke: 0 });
    s.he += p.grenades.he * GRENADE_STOCK.refills;
    s.smoke += p.grenades.smoke * GRENADE_STOCK.refills;
    return { ...p, grenadeIssue: { ...p.grenades } };
  });
  // whole grenades: a fractional refills dial rounds each side's stock down
  for (const t of Object.keys(stocks)) { const x = stocks[Number(t)]; x.he = Math.floor(x.he); x.smoke = Math.floor(x.smoke); }
  return Object.keys(stocks).length ? { ...state, players, grenadeStocks: stocks } : state;
}

/** What a unit is short of its issue (none without one). */
function short(p: Pick<PlayerState, 'grenades' | 'grenadeIssue'>): { he: number; smoke: number } {
  if (!p.grenadeIssue) return { he: 0, smoke: 0 };
  return { he: Math.max(0, p.grenadeIssue.he - (p.grenades?.he ?? 0)), smoke: Math.max(0, p.grenadeIssue.smoke - (p.grenades?.smoke ?? 0)) };
}

/** Out of both kinds, issued some, and the side's stock still holds some of what it was issued: worth a walk to a crate. */
export function grenadesOut(state: Pick<GameState, 'grenadeStocks'>, p: Pick<PlayerState, 'grenades' | 'grenadeIssue' | 'teamIndex'>): boolean {
  if (!p.grenadeIssue || (p.grenades?.he ?? 0) + (p.grenades?.smoke ?? 0) > 0) return false;
  const s = state.grenadeStocks?.[p.teamIndex];
  return !!s && ((p.grenadeIssue.he > 0 && s.he > 0) || (p.grenadeIssue.smoke > 0 && s.smoke > 0));
}

/** Short of its issue in a kind the side's stock still holds: worth asking for at a crate in reach. */
export function grenadesWanted(state: Pick<GameState, 'grenadeStocks'>, p: Pick<PlayerState, 'grenades' | 'grenadeIssue' | 'teamIndex'>): boolean {
  const need = short(p), s = state.grenadeStocks?.[p.teamIndex];
  return !!s && ((need.he > 0 && s.he > 0) || (need.smoke > 0 && s.smoke > 0));
}

/** Top unit `unitId` back up to its issue from its side's stock (242: or `side`'s — the crate's it stands at), as far as it goes. Unchanged (and no event) when nothing is given. */
export function topUpGrenades(state: GameState, unitId: number, at: GrenadeEvent['at'], side?: number): { state: GameState; event: GrenadeEvent | null } {
  const p = state.players.find(x => x.id === unitId);
  const from = side ?? p?.teamIndex ?? 0;   // (242: the stock of the crate's side; the unit's own by default)
  const stock = p ? state.grenadeStocks?.[from] : undefined;
  if (!p || !stock) return { state, event: null };
  const need = short(p);
  const he = Math.min(need.he, stock.he), smoke = Math.min(need.smoke, stock.smoke);
  if (he <= 0 && smoke <= 0) return { state, event: null };
  const grenades = { he: (p.grenades?.he ?? 0) + he, smoke: (p.grenades?.smoke ?? 0) + smoke };
  return {
    state: {
      ...state,
      players: state.players.map(x => (x.id === unitId ? { ...x, grenades } : x)),
      grenadeStocks: { ...state.grenadeStocks, [from]: { he: stock.he - he, smoke: stock.smoke - smoke } },
    },
    event: { kind: 'grenadesRestocked', unitId, he, smoke, at },
  };
}

/** A standing crate, whatever it holds — where the side's grenades are handed out (DECISIONS 406: and a carrier, its own load's). */
export const standingCrate = (s: StructureState): boolean => s.health > 0 && !!s.supply && (!!structureJob(s).supply || carrierLoad(s));

/**
 * DECISIONS 298 (c) (the owner: "Restock and return"): a striker issued HE and out of it, its side's stock still holding
 * some — the nearest crate of its side or linked FOB on its planet to draw more at, however far; else null.
 */
export function strikeRestock(state: GameState, p: Pick<PlayerState, 'grenades' | 'grenadeIssue' | 'teamIndex' | 'planetIndex' | 'surfaceDir'>): StructureState | null {
  if (!(p.grenadeIssue?.he ?? 0) || (p.grenades?.he ?? 0) > 0 || !(state.grenadeStocks?.[p.teamIndex]?.he ?? 0)) return null;
  return nearestTeamCrate(state, p, Infinity, s => standingCrate(s) && servesPool(state, s));
}

/** One tick's crate top-ups: every unit asking (`resupply`), on its feet and out of any seat, within reach of a team crate. Null when nothing changed. */
export function stepGrenadeRestock(state: GameState, asks: ReadonlyArray<boolean | undefined>): { state: GameState; events: GrenadeEvent[] } | null {
  if (!state.grenadeStocks) return null;
  let next = state;
  const events: GrenadeEvent[] = [];
  for (let i = 0; i < state.players.length; i++) {
    const p = next.players[i];
    if (!asks[i] || !p.alive || p.airborne || p.mount || !p.grenadeIssue) continue;
    const crate = nearestCrate(next, p, supplyReach(next), s => standingCrate(s) && servesPool(next, s));   // 69.4: a FOB only while linked
    if (!crate) continue;
    const heAt = carrierHas(crate, 'he'), smokeAt = carrierHas(crate, 'smoke');
    if (heAt !== null && smokeAt !== null) {   // (DECISIONS 406: a carrier's own load, not its side's ledger)
      const need = short(p), he = Math.min(need.he, heAt), smoke = Math.min(need.smoke, smokeAt);
      if (he <= 0 && smoke <= 0) continue;
      const structures = takeFromCarrier(takeFromCarrier(next.structures, crate.id, 'he', he), crate.id, 'smoke', smoke);
      next = { ...next, structures, players: next.players.map(x => (x.id === p.id ? { ...x, grenades: { he: (x.grenades?.he ?? 0) + he, smoke: (x.grenades?.smoke ?? 0) + smoke } } : x)) };
      events.push({ kind: 'grenadesRestocked', unitId: p.id, he, smoke, at: 'crate' });
      continue;
    }
    const r = topUpGrenades(next, p.id, 'crate', crateSide(next, crate, p));   // (242: any side's crate, its side's stock)
    if (r.event) { next = r.state; events.push(r.event); }
  }
  return events.length ? { state: next, events } : null;
}
